import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { AdminService } from 'src/app/core/services/admin.service';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { TableAction, TableColumn } from 'src/app/shared/models/table-config.model';
import {
  EndpointPermission,
  EndpointPermissionUpdate,
} from 'src/app/core/models/endpoint-permission';
import { EndpointPermissionEditModalComponent } from '../endpoint-permission-edit-modal/endpoint-permission-edit-modal.component';

@Component({
  selector: 'app-endpoint-permissions',
  templateUrl: './endpoint-permissions.component.html',
  standalone: false,
  providers: [DialogService],
})
export class EndpointPermissionsComponent implements OnInit {
  allPermissions: EndpointPermission[] = [];
  tableData: EndpointPermission[] = [];
  totalRecords: number = 0;
  loading: boolean = false;
  syncing: boolean = false;
  searchText: string = '';

  ref: DynamicDialogRef | undefined;

  tableColumns: TableColumn[] = [
    {
      field: 'routePath',
      header: 'ROUTE',
      type: 'text',
      width: 'auto',
      sortable: true,
      linkAction: (row) => this.openEditModal(row),
    },
    {
      field: 'httpMethod',
      header: 'METHOD',
      type: 'status',
      width: '110px',
      sortable: true,
      statusConfig: {
        GET: { label: 'GET', cssClass: 'bg-blue-100 text-blue-700' },
        POST: { label: 'POST', cssClass: 'bg-green-100 text-green-700' },
        PUT: { label: 'PUT', cssClass: 'bg-amber-100 text-amber-700' },
        PATCH: { label: 'PATCH', cssClass: 'bg-amber-100 text-amber-700' },
        DELETE: { label: 'DELETE', cssClass: 'bg-red-100 text-red-700' },
      },
    },
    {
      field: 'displayName',
      header: 'NAME',
      type: 'text',
      width: 'auto',
      sortable: true,
    },
    {
      field: 'description',
      header: 'DESCRIPTION',
      type: 'text',
      width: 'auto',
      sortable: false,
    },
    {
      field: 'permissionKey',
      header: 'PERMISSION KEY',
      type: 'text',
      width: 'auto',
      sortable: true,
    },
    {
      field: 'requiresAuthorization',
      header: 'REQUIRES AUTHENTICATION',
      type: 'status',
      width: '110px',
      sortable: true,
      statusConfig: {
        true: { label: 'Required', cssClass: 'bg-indigo-100 text-indigo-700' },
        false: { label: 'Public', cssClass: 'bg-gray-100 text-gray-500' },
      },
    },
    {
      field: 'isActive',
      header: 'FEATURE FLAG',
      type: 'status',
      width: '110px',
      sortable: true,
      statusConfig: {
        true: { label: 'Enabled', cssClass: 'bg-green-100 text-green-700' },
        false: { label: 'Disabled', cssClass: 'bg-gray-100 text-gray-500' },
      },
    },
    { field: 'actions', header: '', type: 'actions', width: 'auto' },
  ];

  tableActions: TableAction[] = [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (row) => this.openEditModal(row),
    },
  ];

  constructor(
    private adminService: AdminService,
    private readonly toastService: AppToastService,
    public dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.loadPermissions();
  }

  handleSearch(text: string): void {
    this.searchText = text?.toLowerCase() || '';
    this.updateTableData();
  }

  loadPermissions(): void {
    this.loading = true;
    this.adminService
      .getEndpointPermissions()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (permissions: EndpointPermission[]) => {
          this.allPermissions = permissions || [];
          this.updateTableData();
        },
        error: () => {},
      });
  }

  syncPermissions(): void {
    this.syncing = true;
    this.adminService
      .syncEndpointPermissions()
      .pipe(finalize(() => (this.syncing = false)))
      .subscribe({
        next: (result) => {
          this.toastService.success(
            'Synced',
            `Created ${result.createdProfiles}, updated ${result.updatedProfiles} permissions.`,
          );
          this.loadPermissions();
        },
        error: () => {},
      });
  }

  updateTableData(): void {
    const filtered = this.searchText
      ? this.allPermissions.filter((p) =>
          [p.routePath, p.displayName, p.description, p.permissionKey, p.httpMethod]
            .join(' ')
            .toLowerCase()
            .includes(this.searchText),
        )
      : [...this.allPermissions];

    this.tableData = filtered;
    this.totalRecords = filtered.length;
  }

  loadLazy(event: TableLazyLoadEvent): void {
    if (!event.sortField) {
      return;
    }

    const sorted = [...this.tableData].sort((a, b) => {
      const valueA = String(a[event.sortField as keyof EndpointPermission] ?? '').toLowerCase();
      const valueB = String(b[event.sortField as keyof EndpointPermission] ?? '').toLowerCase();
      return (event.sortOrder || 1) * valueA.localeCompare(valueB);
    });

    this.tableData = sorted;
  }

  openEditModal(permission: EndpointPermission): void {
    this.ref = this.dialogService.open(EndpointPermissionEditModalComponent, {
      showHeader: false,
      width: '720px',
      styleClass: 'responsive-modal',
      contentStyle: {
        'max-height': '88vh',
        overflow: 'hidden',
        padding: '0',
        'border-radius': '12px',
      },
      data: { permission },
    });

    this.ref.onClose.subscribe((result: any) => {
      if (!result) {
        return;
      }

      const displayName = (result.displayName || '').trim();
      if (!displayName) {
        this.toastService.warning('Validation', 'Display name is required.');
        return;
      }

      const payload: EndpointPermissionUpdate = {
        displayName,
        description: result.description ?? '',
        isActive: result.isActive,
      };

      this.adminService.updateEndpointPermission(result.id, payload).subscribe({
        next: () => {
          this.toastService.success('Saved', 'Permission updated successfully.');
          this.loadPermissions();
        },
        error: () => {},
      });
    });
  }
}
